import { createStaffLeaveSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiBlob, saveBlob } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { formatMinor } from '../../lib/money';
import type { LeaveRequest } from './LeaveTab';

interface Balance {
  leaveTypeId: string;
  name: string;
  annualDays: number | null;
  remainingDays: number | null;
}
interface Slip {
  itemId: string;
  period: string;
  status: string;
  netMinor: number;
  currency: string;
}

/** A staff member's own leave and payslips; nothing here can reach anyone else's data. */
export function MyHrPage() {
  const { canAny } = useAuth();
  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {canAny('hr_leave.apply') && <MyLeave />}
      {canAny('payslips.view_own') && <MyPayslips />}
    </div>
  );
}

function MyLeave() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ leaveTypeId: '', fromDate: '', toDate: '', reason: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const mine = useQuery({
    queryKey: ['me', 'leave'],
    queryFn: () => api<{ balances: Balance[]; requests: LeaveRequest[] }>('/me/leave'),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['me', 'leave'] });
  const apply = useMutation({
    mutationFn: (body: object) => api('/me/leave', { method: 'POST', body }),
    onSuccess: () => {
      setForm({ leaveTypeId: '', fromDate: '', toDate: '', reason: '' });
      setMessage({ kind: 'ok', text: t('hr.sent') });
      refresh();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/me/leave/${id}/cancel`, { method: 'POST' }),
    onSuccess: refresh,
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  // An account that is not linked to a staff member has no leave to ask for: say so and offer no form that cannot work.
  if (mine.isError)
    return (
      <section aria-label={t('hr.myLeave')} className="space-y-4">
        <h1 className="text-2xl font-bold">{t('hr.myLeave')}</h1>
        <Notice kind="error">{errorMessage(mine.error)}</Notice>
      </section>
    );

  return (
    <section aria-label={t('hr.myLeave')} className="space-y-4">
      <h1 className="text-2xl font-bold">{t('hr.myLeave')}</h1>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <ul className="text-sm text-slate-700">
        {mine.data?.balances.map((b) => (
          <li key={b.leaveTypeId}>
            {b.remainingDays === null
              ? t('hr.unlimited', { name: b.name })
              : t('hr.balance', {
                  name: b.name,
                  remaining: b.remainingDays,
                  total: b.annualDays ?? 0,
                })}
          </li>
        ))}
      </ul>
      <form
        noValidate
        aria-label={t('hr.applyLeave')}
        onSubmit={(e) => {
          e.preventDefault();
          const parsed = createStaffLeaveSchema.safeParse(form);
          if (!parsed.success)
            return setErrors(
              Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
            );
          setErrors({});
          setMessage(null);
          apply.mutate(parsed.data);
        }}
        className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
      >
        <div>
          <label htmlFor="leave-type" className="block text-sm font-medium">
            {t('hr.leaveType')}
          </label>
          <select
            id="leave-type"
            value={form.leaveTypeId}
            onChange={(e) => setForm({ ...form, leaveTypeId: e.target.value })}
            className={selectClass}
          >
            <option value="">—</option>
            {mine.data?.balances.map((b) => (
              <option key={b.leaveTypeId} value={b.leaveTypeId}>
                {b.name}
              </option>
            ))}
          </select>
          {errors.leaveTypeId && (
            <p role="alert" className="text-xs text-red-700">
              {errors.leaveTypeId}
            </p>
          )}
        </div>
        <TextField
          label={t('hr.reason')}
          value={form.reason}
          error={errors.reason}
          maxLength={500}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />
        <TextField
          label={t('hr.from')}
          type="date"
          value={form.fromDate}
          error={errors.fromDate}
          onChange={(e) => setForm({ ...form, fromDate: e.target.value })}
        />
        <TextField
          label={t('hr.to')}
          type="date"
          value={form.toDate}
          error={errors.toDate}
          onChange={(e) => setForm({ ...form, toDate: e.target.value })}
        />
        <div>
          <button type="submit" className={primaryButton} disabled={apply.isPending}>
            {t('hr.send')}
          </button>
        </div>
      </form>
      <ul className="space-y-2 text-sm">
        {mine.data?.requests.map((r) => (
          <li
            key={r.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3"
          >
            <span>
              {r.leaveTypeName} ·{' '}
              {r.fromDate === r.toDate ? r.fromDate : `${r.fromDate} → ${r.toDate}`} ·{' '}
              {t('hr.days', { days: r.days })} · {t(`hr.requestStatus.${r.status}` as MessageKey)}
            </span>
            {r.status === 'pending' && (
              <button
                type="button"
                className={secondaryButton}
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(r.id)}
              >
                {t('hr.cancelRequest')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function MyPayslips() {
  const [message, setMessage] = useState<string | null>(null);
  const slips = useQuery({
    queryKey: ['me', 'payslips'],
    queryFn: () => api<Slip[]>('/me/payslips'),
  });
  const download = async (s: Slip) => {
    try {
      saveBlob(await apiBlob(`/me/payslips/${s.itemId}/payslip.pdf`), `payslip-${s.period}.pdf`);
    } catch (e) {
      setMessage(errorMessage(e));
    }
  };
  return (
    <section aria-label={t('hr.myPayslips')} className="space-y-3">
      <h2 className="text-xl font-bold">{t('hr.myPayslips')}</h2>
      {message && <Notice kind="error">{message}</Notice>}
      {slips.data?.length === 0 && <p className="text-slate-600">{t('hr.noPayslips')}</p>}
      <ul className="space-y-2 text-sm">
        {slips.data?.map((s) => (
          <li
            key={s.itemId}
            className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3"
          >
            <span>
              <strong>{s.period}</strong> · {t('hr.net')} {formatMinor(s.netMinor, s.currency)}
            </span>
            <button
              type="button"
              className={secondaryButton}
              aria-label={t('hr.payslipFor', { name: s.period })}
              onClick={() => void download(s)}
            >
              PDF
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
